<template>
    <div>
        <rkm-modal
            extra-large
            ref="RkmModal"
            title="Intervenções e/ou procedimentos clínicos realizados"
        >
            <rkm-form
                ref="RkmForm"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="5">
                        <rkm-form-input-autocomplete
                            :disabled="!!item"
                            :repository="repositories.procedimento"
                            :request-query-string-params="requestQueryStringParams.procedimento"
                            :search-fields="fields.procedimento"
                            :text-field="(item) => `${formatProcedimento(item.codigo)} - ${item.nome}`"
                            :validations="validations.procedimento"
                            @select="handleSelectProcedimento"
                            label="Procedimento"
                            v-model="model.procedimento"
                        />
                    </rkm-col>

                    <rkm-col cols="5">
                        <rkm-form-input-autocomplete
                            :disabled="!model.procedimento || isProcedimentoAbpg"
                            :repository="repositories.cid"
                            :request-query-string-params="requestQueryStringParams.cid"
                            :search-fields="fields.cid"
                            :text-field="(item) => `${formatCid(item.codigo)} - ${item.nome}`"
                            :validations="validations.cid"
                            label="CID"
                            v-model="model.cid"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-form-input
                            :disabled="isProcedimentoAbpg"
                            :validations="validations.quantidade"
                            label="Quantidade"
                            number
                            v-model="model.quantidade"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-textarea
                            :validations="validations.observacao"
                            label="Observações"
                            rows="4"
                            v-model="model.observacao"
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-form>

            <rkm-form v-if="model.material && !isProcedimentoAbpg"
                ref="RkmFormMateriais"
            >

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :repository="repositories.material"
                            :request-query-string-params="requestQueryStringParams.material"
                            :search-fields="fields.material"
                            :text-field="(item) => item.descricaoCompleta"
                            label="Material"
                            v-model="model.material.material"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-form-input
                            :value="model.material.estoque"
                            disabled
                            label="Estoque"
                        >
                            <rkm-input-group-append slot="append">
                                <rkm-button
                                    :disabled="!model.material.material"
                                    @click="loadEstoque"
                                    color="primary"
                                    title="Atualizar estoque"
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="sync-alt" />
                                </rkm-button>
                            </rkm-input-group-append>
                        </rkm-form-input>
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-input
                            :validations="validations.material.quantidade"
                            label="Quantidade"
                            type="number"
                            v-model="model.material.quantidade"
                        >
                            <rkm-input-group-append
                                slot="append"
                            >
                                <rkm-button
                                    :disabled="!quantidadeValida"
                                    @click="$refs.ModalLote.open(model.material.entradaItens)"
                                    title="Visualizar lotes"
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="box-open" />
                                </rkm-button>
                            </rkm-input-group-append>
                        </rkm-form-input>
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :disabled="!quantidadeValida"
                            :label="false"
                            @click="$refs.RkmFormMateriais.validate(editIndex !== null ? updateMaterial : add)"
                            color="primary"
                        >
                                {{ editIndex !== null ? 'Editar' : 'Incluir' }}
                        </rkm-button>
                    </rkm-col>

                </rkm-row>

                <rkm-area title="Materiais">
                        <rkm-table
                            :fields="fields.materiais"
                            :items="model.materiais"
                            hide-filters
                            hide-pagination
                        >

                            <template v-slot:cell(actions)="{item, index}">

                                <rkm-button
                                    @click="editMaterial(item, index)"
                                    color="primary"
                                    small
                                    title="Editar"
                                >
                                    <rkm-icon icon="pen" />
                                </rkm-button>

                                <rkm-button
                                    @click="destroy(index)"
                                    color="danger"
                                    small
                                    title="Excluir"
                                >
                                    <rkm-icon icon="trash" />
                                </rkm-button>
                            </template>
                        </rkm-table>
                </rkm-area>
            </rkm-form>

            <template v-slot:footer="{cancel}">
                <rkm-button @click="cancel">
                    Fechar
                </rkm-button>

                <rkm-button
                    @click="save"
                    color="primary"
                >
                    {{item ? 'Salvar' : 'Incluir'}}
                </rkm-button>
            </template>
        </rkm-modal>

        <ModalLote
            :model="model.material"
            ref="ModalLote"
        />

    </div>
</template>

<script>

// Components.
import ModalLote from '@/components/almoxarifado/components/lote/Modal'

// Enums.
import procedimento from '@/enums/sigtap/procedimento'

// Mixins.
import {formats, object, repository} from '@/mixins'

//Vuex
import {mapActions} from 'vuex'

export default {
    components: {ModalLote},
    computed: {
        requestQueryStringParams() {
            return {
                procedimento: {
                    cbo_lotacao_atual: true,
                    paciente_id: this.data.additionals.paciente.id,
                    competencia_id: this.data.additionals.isRetroativo ? this.data.additionals.agendamentoCompetenciaId : null,
                    with_codigo_ab: true,
                    where_not: {
                        codigo: [
                            this.enums.procedimento.AVALIACAO_ANTROPOMETRICA,
                            this.enums.procedimento.GLICEMIA_CAPILAR,
                            this.enums.procedimento.MEDICAO_DE_ALTURA,
                            this.enums.procedimento.MEDICAO_DE_PESO,
                            this.enums.procedimento.AFERICAO_DE_TEMPERATURA,
                            this.enums.procedimento.AFERICAO_PRESSAO_ARTERIAL,
                        ],
                    },
                },
                cid: this.model.procedimento && this.model.procedimento.hasCidPrincipal
                    ? {
                        principal: true,
                        procedimento_id: this.model.procedimento.id,
                    }
                    : {}
            }
        },
        isProcedimentoAbpg() {
            return !!(this.model.procedimento && this.model.procedimento.origem === 'abpg')
        },
        validations() {
            return {
                procedimento: {required: true},
                cid: {required: this.model.procedimento && !!this.model.procedimento.hasCidPrincipal},
                quantidade: {
                    required: true,
                    min_value: 1,
                    max: 3,
                    max_value: this.isProcedimentoAbpg ? 1 : 3,
                },
                material: {
                    quantidade: {
                        required: !!this.model.material.material,
                        min_value: 1,
                        max_value: this.model.material.estoque,
                    },
                },
                observacao: {max: 500},
            }
        },        
        quantidadeValida() {
            const quantidade = parseInt(this.model.material.quantidade)

            return quantidade > 0 && quantidade <= this.model.material.estoque
        },
    },
    created() {
        this.repositories = {
            cid: this.getConectaSusRepository('sigtap.cid'),
            procedimento: this.getConectaSusRepository('sigtap.procedimento'),
            material: this.getConectaSusRepository('cadastro.material'),
        }
    },
    data: () => ({
        enums: {procedimento},
        editIndex: null,
        fields: {
            procedimento: [
                {key: 'codigo', searchable: true},
                {key: 'nome', searchable: true},
            ],
            cid: [
                {key: 'codigo', searchable: true},
                {key: 'nome', searchable: true},
            ],
            material: [
                {key: "codigo_material", searchable: true},
                {key: "descricao", searchable: true},
            ],
            materiais: [
                {key: "descricaoCompleta", label: 'Material', searchable: true},
                {key: "quantidade", label: 'Quantidade', searchable: true},
                {class: 'col-min text-center', key: 'actions', label: 'Ações'},
            ],
        },
        form: {
            possuiEntradaComLote: false,
        },
        item: null,
        model: {
            procedimento: null,
            cid: null,
            quantidade: 1,
            observacao: null,
            material: {
                entradaItens: [],
                escolheuEntradaItemManualmente: null,
                estoque: null,
                material: null,
                quantidade: null,
            },
            materiais: [],
        },
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        open(item = null) {
            this.item = item

            if (this.item) {
                this.edit()
            } else {
                this.create()
            }

            this.$refs.RkmModal.show()
        },
        create() {
            this.resetForm()
        },
        handleSelectProcedimento() {
            this.model.cid = null

            if (this.isProcedimentoAbpg) {
                this.model.quantidade = 1
            }
        },
        loadEstoque() {
            this.form.possuiEntradaComLote = false
            this.model.material.entradaItens = []
            this.model.material.escolheuEntradaItemManualmente = false
            this.model.material.estoque = 0
            this.model.material.quantidade = null

            this.setLoading()

            this.repositories.material.getEstoque(this.model.material.material.id)
                .then(({data}) => {
                    this.form.possuiEntradaComLote = data.possuiEntradaComLote
                    this.model.material.estoque = data.total
                    this.model.material.entradaItens = data.porEntrada.map(estoqueEntrada => {
                        estoqueEntrada.quantidadeSaida = 0
                        return estoqueEntrada
                    })
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        selectEntradaItens() {
            this.model.material.escolheuEntradaItemManualmente = false
            this.model.material.entradaItens.map(e => e.quantidadeSaida = 0)

            let quantidade = parseInt(this.model.material.quantidade)
            let i = 0

            while (quantidade > 0) {
                const entradaItem = this.model.material.entradaItens[i]
                const estoque = parseInt(entradaItem.estoque)

                if (quantidade > estoque) {
                    entradaItem.quantidadeSaida = estoque
                    quantidade -= estoque
                } else {
                    entradaItem.quantidadeSaida = quantidade
                    quantidade = 0
                }

                i++
            }
        },
        edit() {

            this.model = this.clone({
                procedimento: this.item.procedimento,
                cid: this.item.cid,
                quantidade: this.item.quantidade,
                observacao: this.item.observacao,
                material: {
                    entradaItens: [],
                    escolheuEntradaItemManualmente: null,
                    estoque: null,
                    material: null,
                    quantidade: null,
                },
                materiais: this.item.procedimento.materiais || [], 
            })
        },
        store() {
            this.data.model.procedimentosClinicos.push({
                procedimento: {
                    id: this.model.procedimento.id,
                    codigo: this.model.procedimento.codigo,
                    nome: this.model.procedimento.nome,
                    hasInstrumentoRegistroRaas: !!this.model.procedimento.hasInstrumentoRegistroRaas,
                    hasCidPrincipal: this.model.procedimento.hasCidPrincipal,
                    origem: this.model.procedimento.origem || 'sigtap',
                    materiais: this.model.materiais,
                },
                cid: this.model.cid ? {
                    id: this.model.cid.id,
                    codigo: this.model.cid.codigo,
                    nome: this.model.cid.nome,
                } : null,
                automatico: false,
                quantidade: this.model.quantidade,
                observacao: this.model.observacao,
            })
        },
        update() {
            Object.assign(this.item, {
                procedimento: {
                    id: this.model.procedimento.id,
                    codigo: this.model.procedimento.codigo,
                    nome: this.model.procedimento.nome,
                    hasInstrumentoRegistroRaas: !!this.model.procedimento.hasInstrumentoRegistroRaas,
                    hasCidPrincipal: this.model.procedimento.hasCidPrincipal,
                    origem: this.model.procedimento.origem || 'sigtap',
                    materiais: this.model.materiais,
                },
                cid: this.model.cid ? {
                    id: this.model.cid.id,
                    codigo: this.model.cid.codigo,
                    nome: this.model.cid.nome,
                } : null,
                automatico: false,
                quantidade: this.model.quantidade,
                observacao: this.model.observacao,
            })
        },
        add() {
            this.model.materiais.push({
                id: this.model.material.material.id,
                descricaoCompleta: this.model.material.material.descricaoCompleta,
                quantidade: this.model.material.quantidade, 
                materialEscolheuEntradaItemManualmente: this.model.material.escolheuEntradaItemManualmente,
                materialEntradaItens: this.model.material.entradaItens,
            })

            this.clearMaterial()
        },
        editMaterial(material, index) {
            this.editIndex = index

            this.model.material.material = {
                id: material.id,
                descricaoCompleta: material.descricaoCompleta,
            }

            this.model.material.quantidade = material.quantidade 
        },
        updateMaterial() {

            if (this.editIndex === null) return

            this.$set(this.model.materiais, this.editIndex, {
                id: this.model.material.material.id,
                descricaoCompleta: this.model.material.material.descricaoCompleta,
                quantidade: this.model.material.quantidade,
            })

            this.editIndex = null

            this.clearMaterial()
        },
        clearMaterial() {

            this.model.material.entradaItens = []
            this.model.material.escolheuEntradaItemManualmente = null
            this.model.material.estoque = null
            this.model.material.material = null
            this.model.material.quantidade = null 

            this.$nextTick().then(() => this.$refs.RkmFormMateriais.resetValidator())
        },
        destroy(index) {
            this.showDeleteConfirmation('Deseja realmente excluir o material?', async () => {
                this.model.materiais.splice(index, 1)
            })
        },
        save() {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid === false) return

                    const origem = this.model.procedimento.origem || 'sigtap'

                    const jaIncluidoEmClinicos = this.data.model.procedimentosClinicos.find(procedimentoClinico =>
                        procedimentoClinico.procedimento.id === this.model.procedimento.id
                        && (procedimentoClinico.procedimento.origem || 'sigtap') === origem
                    )

                    const jaIncluidoEmAdministrativos = origem === 'abpg'
                        && (this.data.procedimentosAdministrativosModel || []).find(procedimentoAdministrativo =>
                            procedimentoAdministrativo.procedimento.origem === 'abpg'
                            && procedimentoAdministrativo.procedimento.id === this.model.procedimento.id
                        )

                    if (!this.item && (jaIncluidoEmClinicos || jaIncluidoEmAdministrativos)) {
                        this.showValidationNotification('O procedimento já foi incluído nos procedimentos clínicos ou administrativos.')
                        return
                    }


                    if (this.item) {
                        this.update()
                    } else {
                        this.store()
                    }

                    this.$refs.RkmModal.hide()
                })
        },
        resetForm() {
            this.model.procedimento = null
            this.model.cid = null
            this.model.quantidade = 1
            this.model.observacao = null                    
        
            this.model.material.entradaItens = []
            this.model.material.escolheuEntradaItemManualmente = null
            this.model.material.estoque = null
            this.model.material.material = null
            this.model.material.quantidade = null
            this.model.materiais = []
        },
    },
    mixins: [formats, object, repository],
    props: {
        data: {required: true, type: Object},
    },
    watch: {
        'model.material.material'(value) {
            this.form.possuiEntradaComLote = false
            this.model.material.entradaItens = []
            this.model.material.escolheuEntradaItemManualmente = false
            this.model.material.estoque = 0
            this.model.material.quantidade = null

            if (value && value.id) {
                this.loadEstoque()
            }
        },
        'model.material.quantidade': function () {
            if (this.form.possuiEntradaComLote && this.quantidadeValida) {
                this.selectEntradaItens()
            }
        },
    },
}
</script>